import { AxiosError } from "axios";

/**
 * Hata detaylarını içeren interface
 */
export interface ErrorDetails {
    status?: number;
    message: string;
    errors?: string[];
    details?: unknown;
}

/**
 * Axios hata mesajını formatlar (eski API uyumluluğu için)
 */
export function formatErrorMessage(error: AxiosError): string {
    const details = extractErrorDetails(error);
    return details.message;
}

/**
 * Network hatalarını parse et
 */
function parseNetworkError(error: AxiosError): ErrorDetails | null {
    if (error.response) return null; // Backend response var, network hatası değil

    if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        return {
            message: 'Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin.',
            errors: ['NETWORK_ERROR'],
        };
    }

    if (error.code === 'ETIMEDOUT') {
        return {
            message: 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.',
            errors: ['TIMEOUT'],
        };
    }

    return {
        message: error.message || 'Bilinmeyen bir hata oluştu',
        errors: ['UNKNOWN_ERROR'],
    };
}

/**
 * Response data'dan hata mesajı çıkar
 */
function parseResponseData(responseData: unknown, status?: number): { message: string; errors: string[]; details?: unknown; hasDetailMessage?: boolean } {
    let message = status ? `HTTP ${status}` : 'Hata oluştu';
    const errors: string[] = [];
    let details: unknown;
    let hasDetailMessage = false;

    if (!responseData || typeof responseData !== 'object') {
        return {
            message: typeof responseData === 'string' ? responseData : message,
            errors
        };
    }

    const data = responseData as Record<string, unknown>;

    // Problem Details format (RFC 7807)
    if (data.title || data.detail) {
        hasDetailMessage = true;
        
        // RFC 7807 validation errors object'ini parse et
        if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
            const validationErrors = parseValidationErrors(data.errors as Record<string, unknown>);
            errors.push(...validationErrors);
            
            // Validation errors varsa, mesajı daha açıklayıcı yap
            if (validationErrors.length > 0) {
                message = validationErrors.length === 1 
                    ? validationErrors[0] 
                    : `${data.title || 'Doğrulama hatası'}`;
            } else if (data.detail) {
                // Sadece detail mesajını kullan, title ekleme
                message = data.detail as string;
            } else {
                message = (data.title as string) || message;
            }
        } else if (data.detail) {
            // Sadece detail mesajını kullan
            message = data.detail as string;
        } else {
            message = (data.title as string) || message;
        }
        
        details = data;
    }
    // errorMessage field'ı (BadRequest için)
    else if (data.errorMessage) {
        message = data.errorMessage as string;
        hasDetailMessage = true;
        details = data;
    }
    // BaseResponse format
    else if (data.message) {
        message = data.message as string;
        if (data.errors && Array.isArray(data.errors)) {
            errors.push(...(data.errors as string[]));
        }
        details = data;
    }
    // Error field
    else if (data.error || data.errors) {
        const result = parseErrorField(data.error || data.errors);
        message = result.message;
        errors.push(...result.errors);
        details = data;
    }

    return { message, errors, details, hasDetailMessage };
}

/**
 * RFC 7807 validation errors object'ini parse et
 * Örnek: { "FirstName": ["İsim en fazla 128 karakter olmalı."], "Email": ["Geçerli bir email girin"] }
 */
function parseValidationErrors(errorsObject: Record<string, unknown>): string[] {
    const errorMessages: string[] = [];
    
    Object.entries(errorsObject).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
            // Her field için tüm mesajları ekle
            messages.forEach(msg => {
                if (typeof msg === 'string') {
                    errorMessages.push(`${field}: ${msg}`);
                }
            });
        } else if (typeof messages === 'string') {
            // Tek bir mesaj ise direkt ekle
            errorMessages.push(`${field}: ${messages}`);
        }
    });
    
    return errorMessages;
}

/**
 * Error field'ını parse et (string | array | object)
 */
function parseErrorField(errorField: unknown): { message: string; errors: string[] } {
    if (typeof errorField === 'string') {
        return { message: errorField, errors: [errorField] };
    }

    if (Array.isArray(errorField)) {
        const messages = errorField.filter(e => typeof e === 'string') as string[];
        return {
            message: messages.join(', '),
            errors: messages
        };
    }

    if (errorField && typeof errorField === 'object') {
        const validationErrors = Object.entries(errorField as Record<string, unknown>).map(
            ([field, msg]) => `${field}: ${msg}`
        );
        return {
            message: validationErrors.join(', '),
            errors: validationErrors
        };
    }

    return { message: 'Hata oluştu', errors: [] };
}

/**
 * HTTP status koduna göre user-friendly mesaj
 */
function getStatusMessage(status: number, hasErrors: boolean): string | null {
    if (hasErrors) return null; // Backend'den mesaj varsa kullanma

    const messages: Record<number, string> = {
        400: 'Geçersiz istek',
        401: 'Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.',
        403: 'Bu işlem için yetkiniz yok',
        404: 'İstenen kaynak bulunamadı',
        409: 'Çakışan işlem. Bu kayıt zaten mevcut.',
        422: 'Geçersiz veri formatı',
        429: 'Çok fazla istek gönderildi. Lütfen bekleyin.',
        500: 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.',
        502: 'Sunucu geçici olarak kullanılamıyor',
        503: 'Servis şu anda bakımda',
    };

    return messages[status] || null;
}

/**
 * Axios hatasından detaylı bilgi çıkar
 */
export function extractErrorDetails(error: AxiosError): ErrorDetails {
    // Network hatası kontrolü
    const networkError = parseNetworkError(error);
    if (networkError) return networkError;

    // Backend response var
    const status = error.response?.status;
    const responseData = error.response?.data;

    const parsed = parseResponseData(responseData, status);

    // Status kod bazlı mesaj varsa kullan
    // Ancak backend'den detail mesajı geldiyse fallback mesajı gösterme
    if (status && !parsed.hasDetailMessage) {
        const statusMsg = getStatusMessage(status, parsed.errors.length > 0);
        if (statusMsg) {
            parsed.message = statusMsg;
        }
    }

    const result = {
        status,
        message: parsed.message,
        errors: parsed.errors.length > 0 ? parsed.errors : undefined,
        details: parsed.details,
    };

    // Debug mode'da detaylı error log
    if (typeof window !== 'undefined' && (window as Window & { APP_DEBUG?: boolean }).APP_DEBUG) {
        console.group('[Error Details - Debug Mode]');
        console.error('Status:', result.status);
        console.error('Message:', result.message);
        if (result.errors) {
            console.error('Errors:', result.errors);
        }
        if (result.details) {
            console.error('Details:', result.details);
        }
        console.error('Original Error:', error);
        console.groupEnd();
    }

    return result;
}

/**
 * JSON hata mesajını formatlar
 */
async function formatJsonError(response: Response): Promise<string> {
    try {
        const errorData = await response.json();
        if (errorData.title || errorData.detail || errorData.message) {
            const message = `${errorData.title || errorData.message || ''} ${errorData.detail || ''}`.trim();
            return message ? `${message} (HTTP ${response.status})` : `HTTP ${response.status}`;
        }
    } catch {
        // JSON parse hatası durumunda varsayılan mesajı kullan
    }
    return `HTTP ${response.status}`;
}

/**
 * Text hata mesajını formatlar
 */
async function formatTextError(response: Response): Promise<string> {
    try {
        const errorText = await response.text();
        return errorText ? `${errorText} (HTTP ${response.status})` : `HTTP ${response.status}`;
    } catch {
        // Text parse hatası durumunda varsayılan mesajı kullan
        return `HTTP ${response.status}`;
    }
}

/**
 * Başarılı response'u işler
 */
async function handleSuccessResponse<T>(response: Response, isJson: boolean): Promise<T> {
    if (isJson) {
        const data = await response.json();
        return data;
    } else {
        const data = await response.text();
        // Eğer T bir BaseResponse ise, BaseResponse formatında dön
        return { isSuccess: true, data } as T;
    }
}

/**
 * Fetch API Response'unu BaseResponse formatına çevirir
 */
export async function handleFetchResponse<T>(
    response: Response,
    options?: { onUnauthorized?: () => Promise<void> }
): Promise<T> {
    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');

    // 401 durumunda onUnauthorized callback'i çağır
    if (response.status === 401 && options?.onUnauthorized) {
        await options.onUnauthorized();
    }

    // Başarılı response'lar için
    if (response.ok) {
        return handleSuccessResponse<T>(response, isJson);
    }

    // Hatalı response'lar için
    const errorMessage = isJson
        ? await formatJsonError(response)
        : await formatTextError(response);

    throw new Error(errorMessage);
}
